import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  useMediaQuery,
  Snackbar,
  Alert,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import PageLayout from "../components/navFoot/PageLayout";
import AvatarPlayer from "../components/digitalTwin/AvatarPlayer";
import SlideViewer from "../components/digitalTwin/SlideViewer";
import QAPanel from "../components/digitalTwin/QAPanel";
import { Room, RoomEvent } from "livekit-client";

const KEEP_ALIVE_INTERVAL = 3 * 60 * 1000; // 3 minutes

// LiveAvatar configuration — calls API directly until backend is running
const LIVEAVATAR_API = "https://api.liveavatar.com/v1";
const LIVEAVATAR_API_KEY = "d1a8a976-64a3-4e8a-926f-02fd75f1b18e";
const AVATAR_ID = "1c690fe7-23e0-49f9-bfba-14344450285b"; // Test avatar — swap back to custom Avneesh later
const VOICE_ID = "1f0122fe-fb3c-4eab-a132-7446ce27cb3b"; // Avneesh Khosla cloned voice (ElevenLabs)
const CONTEXT_ID = "a2825d41-29e2-44a4-9e36-a9340e04003f"; // Vi CMO personality context
const IS_SANDBOX = false; // Production mode — uses real credits (2 credits/min)

// Placeholder slides — replace with actual content
const DEFAULT_SLIDES = [
  {
    index: 0,
    title: "Welcome",
    content: "Welcome to the Vi Digital Twin Experience",
    talking_point:
      "Namaste! I'm Avneesh Khosla, Chief Marketing Officer at Vi — that's pronounced 'We'. Welcome to this interactive session. I'm excited to share our vision with you today.",
  },
  {
    index: 1,
    title: "Vi Brand Vision",
    content: "Vi — Together for Tomorrow",
    talking_point:
      "At Vi, we believe in the power of togetherness. Our brand represents a unified vision — connecting over 200 million Indians with not just a network, but a promise of a better digital tomorrow.",
  },
  {
    index: 2,
    title: "Q&A",
    content: "Ask me anything",
    talking_point:
      "Now I'd love to hear from you. Feel free to ask me anything about Vi's strategy, our brand journey, or the telecom landscape in India. Go ahead!",
  },
];

const VIDigitalTwinCMO = () => {
  const isMobile = useMediaQuery("(max-width:900px)");
  const [room, setRoom] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [slides] = useState(DEFAULT_SLIDES);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPresenting, setIsPresenting] = useState(false);
  const [isAvatarSpeaking, setIsAvatarSpeaking] = useState(false);
  const [isMicActive, setIsMicActive] = useState(true);
  const [transcripts, setTranscripts] = useState([]);
  const [error, setError] = useState(null);
  const [sessionActive, setSessionActive] = useState(false);

  const keepAliveRef = useRef(null);
  const sessionIdRef = useRef(null);
  const presentingSlideRef = useRef(0);
  const roomRef = useRef(null);
  const encoderRef = useRef(new TextEncoder());
  const decoderRef = useRef(new TextDecoder());

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (keepAliveRef.current) clearInterval(keepAliveRef.current);
      stopSession();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendDataChannelEvent = useCallback(
    (eventType, payload = {}) => {
      if (!roomRef.current) return;
      const event = {
        event_id: crypto.randomUUID(),
        event_type: eventType,
        session_id: sessionIdRef.current,
        ...payload,
      };
      const data = encoderRef.current.encode(JSON.stringify(event));
      roomRef.current.localParticipant.publishData(data, {
        reliable: true,
        topic: "agent-control",
      });
    },
    []
  );

  const handleDataReceived = useCallback(
    (payload, participant, kind, topic) => {
      if (topic !== "agent-response") return;
      try {
        const event = JSON.parse(decoderRef.current.decode(payload));
        switch (event.event_type) {
          case "avatar.speak_started":
            setIsAvatarSpeaking(true);
            break;
          case "avatar.speak_ended":
            setIsAvatarSpeaking(false);
            // Auto-advance slide during presentation
            if (isPresenting) {
              const nextSlide = presentingSlideRef.current + 1;
              if (nextSlide < slides.length) {
                presentingSlideRef.current = nextSlide;
                setCurrentSlide(nextSlide);
                setTimeout(() => {
                  sendDataChannelEvent("avatar.speak_text", {
                    text: slides[nextSlide].talking_point,
                  });
                }, 1000);
              } else {
                setIsPresenting(false);
              }
            }
            break;
          case "user.transcription":
            if (event.text) {
              setTranscripts((prev) => [
                ...prev,
                { role: "user", text: event.text },
              ]);
            }
            break;
          case "avatar.transcription":
            if (event.text) {
              setTranscripts((prev) => [
                ...prev,
                { role: "avatar", text: event.text },
              ]);
            }
            break;
          case "session.stopped":
            setSessionActive(false);
            setIsConnected(false);
            break;
          default:
            break;
        }
      } catch (e) {
        console.error("Failed to parse data channel event:", e);
      }
    },
    [isPresenting, slides, sendDataChannelEvent]
  );

  const startSession = async () => {
    setIsStarting(true);
    setError(null);
    try {
      // Step 1: Create session token directly with LiveAvatar API
      const tokenPayload = {
        mode: "FULL",
        avatar_id: AVATAR_ID,
        is_sandbox: IS_SANDBOX,
        max_session_duration: 600,
        interactivity_type: "CONVERSATIONAL",
        avatar_persona: {
          voice_id: VOICE_ID,
          context_id: CONTEXT_ID,
          language: "en",
          voice_settings: {
            provider: "elevenLabs",
            speed: 1.0,
            stability: 0.75,
            similarity_boost: 0.75,
            style: 0,
            use_speaker_boost: true,
            model: "eleven_flash_v2_5",
          },
          stt_config: {
            provider: "deepgram",
          },
        },
      };

      console.log("[DigitalTwin] Creating session token...", tokenPayload);
      const tokenRes = await fetch(`${LIVEAVATAR_API}/sessions/token`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-KEY": LIVEAVATAR_API_KEY,
        },
        body: JSON.stringify(tokenPayload),
      });
      const tokenData = await tokenRes.json();
      console.log("[DigitalTwin] Token response:", tokenData);
      if (tokenData.code !== 1000 || !tokenData.data) {
        throw new Error(tokenData.message || "Failed to create session token");
      }

      const { session_id, session_token } = tokenData.data;
      setSessionId(session_id);
      sessionIdRef.current = session_id;

      // Step 2: Start session
      console.log("[DigitalTwin] Starting session:", session_id);
      const startRes = await fetch(`${LIVEAVATAR_API}/sessions/start`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session_token}`,
        },
      });
      const startData = await startRes.json();
      console.log("[DigitalTwin] Start response:", startData);
      if (startData.code !== 1000 || !startData.data) {
        throw new Error(startData.message || "Failed to start session");
      }

      const { livekit_url, livekit_client_token } = startData.data;
      console.log("[DigitalTwin] Connecting to LiveKit:", livekit_url);

      // Step 3: Connect to LiveKit room
      const newRoom = new Room();
      roomRef.current = newRoom;

      newRoom.on(RoomEvent.DataReceived, handleDataReceived);
      newRoom.on(RoomEvent.Connected, () => {
        setIsConnected(true);
        setSessionActive(true);
      });
      newRoom.on(RoomEvent.Disconnected, () => {
        setIsConnected(false);
        setSessionActive(false);
      });

      await newRoom.connect(livekit_url, livekit_client_token);
      console.log("[DigitalTwin] LiveKit connected!");

      // Enable microphone
      await newRoom.localParticipant.setMicrophoneEnabled(true);
      console.log("[DigitalTwin] Microphone enabled");

      setRoom(newRoom);

      // Start keep-alive pings
      keepAliveRef.current = setInterval(() => {
        fetch(`${LIVEAVATAR_API}/sessions/keep-alive`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-API-KEY": LIVEAVATAR_API_KEY,
          },
          body: JSON.stringify({ session_id }),
        }).catch(console.error);
      }, KEEP_ALIVE_INTERVAL);
    } catch (err) {
      console.error("Failed to start session:", err);
      setError(err.message);
    } finally {
      setIsStarting(false);
    }
  };

  const stopSession = async () => {
    if (keepAliveRef.current) {
      clearInterval(keepAliveRef.current);
      keepAliveRef.current = null;
    }

    if (roomRef.current) {
      roomRef.current.disconnect();
      roomRef.current = null;
    }

    const sid = sessionIdRef.current;
    if (sid) {
      try {
        await fetch(`${LIVEAVATAR_API}/sessions/stop`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-API-KEY": LIVEAVATAR_API_KEY,
          },
          body: JSON.stringify({ session_id: sid, reason: "USER_CLOSED" }),
        });
      } catch (e) {
        console.error("Failed to stop session:", e);
      }
    }

    setRoom(null);
    setSessionId(null);
    sessionIdRef.current = null;
    setIsConnected(false);
    setSessionActive(false);
    setIsPresenting(false);
    setIsAvatarSpeaking(false);
  };

  const startPresentation = () => {
    if (!slides.length) return;
    setIsPresenting(true);
    setCurrentSlide(0);
    presentingSlideRef.current = 0;
    sendDataChannelEvent("avatar.speak_text", {
      text: slides[0].talking_point,
    });
  };

  const handleSendText = (text) => {
    setTranscripts((prev) => [...prev, { role: "user", text }]);
    sendDataChannelEvent("avatar.speak_response", { text });
  };

  const handleInterrupt = () => {
    sendDataChannelEvent("avatar.interrupt");
    setIsAvatarSpeaking(false);
    setIsPresenting(false);
  };

  const handleToggleMic = async () => {
    if (!roomRef.current) return;
    const newState = !isMicActive;
    await roomRef.current.localParticipant.setMicrophoneEnabled(newState);
    setIsMicActive(newState);
  };

  return (
    <PageLayout>
      <Box
        sx={{
          mt: isMobile ? "68px" : "100px",
          px: { xs: 2, md: 4 },
          pb: 4,
          maxWidth: "1400px",
          mx: "auto",
        }}
      >
        {/* Header */}
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <Typography
            variant="h4"
            sx={{
              color: "#fff",
              fontWeight: 700,
              mb: 1,
              fontSize: { xs: "1.5rem", md: "2rem" },
            }}
          >
            Digital Twin — Avneesh Khosla
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: "rgba(255,255,255,0.6)", mb: 2 }}
          >
            Chief Marketing Officer, Vi (Vodafone Idea)
          </Typography>

          {/* Session controls */}
          {!sessionActive ? (
            <Button
              variant="contained"
              onClick={startSession}
              disabled={isStarting}
              startIcon={<PlayArrowIcon />}
              sx={{
                backgroundColor: "#7C4DFF",
                borderRadius: "24px",
                px: 4,
                py: 1.2,
                fontWeight: 600,
                textTransform: "none",
                fontSize: "1rem",
                "&:hover": { backgroundColor: "#651FFF" },
                "&:disabled": { backgroundColor: "#444" },
              }}
            >
              {isStarting ? "Starting..." : "Start Session"}
            </Button>
          ) : (
            <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
              {!isPresenting && slides.length > 0 && (
                <Button
                  variant="contained"
                  onClick={startPresentation}
                  startIcon={<PlayArrowIcon />}
                  sx={{
                    backgroundColor: "#7C4DFF",
                    borderRadius: "24px",
                    px: 3,
                    textTransform: "none",
                    fontWeight: 600,
                    "&:hover": { backgroundColor: "#651FFF" },
                  }}
                >
                  Present Slides
                </Button>
              )}
              <Button
                variant="outlined"
                onClick={stopSession}
                startIcon={<StopIcon />}
                sx={{
                  borderColor: "#FF5252",
                  color: "#FF5252",
                  borderRadius: "24px",
                  px: 3,
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": {
                    borderColor: "#FF1744",
                    backgroundColor: "rgba(255,82,82,0.08)",
                  },
                }}
              >
                End Session
              </Button>
            </Box>
          )}
        </Box>

        {/* Main content: Avatar + Slides */}
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: 3,
            mb: 3,
          }}
        >
          {/* Avatar video */}
          <Box sx={{ flex: 1, minHeight: "360px" }}>
            <AvatarPlayer room={room} isConnected={isConnected} isStarting={isStarting} sessionActive={sessionActive} />
          </Box>

          {/* Slide viewer */}
          {slides.length > 0 && (
            <Box sx={{ flex: 1, minHeight: "360px" }}>
              <SlideViewer
                slides={slides}
                currentSlide={currentSlide}
                onSlideChange={setCurrentSlide}
              />
            </Box>
          )}
        </Box>

        {/* Q&A Panel */}
        {sessionActive && (
          <QAPanel
            onSendText={handleSendText}
            onInterrupt={handleInterrupt}
            transcripts={transcripts}
            isAvatarSpeaking={isAvatarSpeaking}
            isMicActive={isMicActive}
            onToggleMic={handleToggleMic}
          />
        )}

        {/* Error snackbar */}
        <Snackbar
          open={!!error}
          autoHideDuration={6000}
          onClose={() => setError(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => setError(null)}
            severity="error"
            variant="filled"
          >
            {error}
          </Alert>
        </Snackbar>
      </Box>
    </PageLayout>
  );
};

export default VIDigitalTwinCMO;
